import { __importDefault, __importStar } from "tslib";
import { Data, SigInfo, ValidityPeriod } from "@ndn/packet";
import { assert } from "@ndn/util";
import _cjsDefaultImport0 from "@yoursunny/asn1"; const asn1 = __importDefault(_cjsDefaultImport0).default;
import * as CertNaming from "./naming_browser.js";
const ContentTypeKEY = 0x02;
/**
 * NDN Certificate v2.
 *
 * @remarks
 * This type is immutable.
 */
export class Certificate {
    data;
    validity;
    /**
     * Construct Certificate from Data packet.
     *
     * @throws Error
     * Thrown if the Data packet is not a certificate.
     */
    static fromData(data) {
        const { name, contentType, sigInfo: { validity } } = data;
        assert(CertNaming.isCertName(name), `${name} is not a certificate name`);
        assert(contentType === ContentTypeKEY, "ContentType must be KEY");
        assert(validity, "ValidityPeriod is missing");
        return new Certificate(data, validity);
    }
    constructor(data, validity) {
        this.data = data;
        this.validity = validity;
    }
    /** Certificate name aka Data packet name. */
    get name() { return this.data.name; }
    /** KeyLocator name, if present. */
    get issuer() {
        return this.data.sigInfo.keyLocator?.name;
    }
    /**
     * Whether this is a self-signed certificate.
     *
     * @remarks
     * A certificate is considered self-signed if its issuer key name is same as the certificate's
     * key name, i.e. they are the same key.
     */
    get isSelfSigned() {
        return !!this.issuer &&
            CertNaming.toKeyName(this.issuer).equals(CertNaming.toKeyName(this.name));
    }
    /**
     * Ensure certificate is within validity period.
     *
     * @throws Error
     * Certificate has expired as of `now`.
     */
    checkValidity(now = Date.now()) {
        if (!this.validity.includes(now)) {
            throw new Error(`certificate ${this.name} has expired`);
        }
    }
    /** Public key in SubjectPublicKeyInfo (SPKI) binary format. */
    get publicKeySpki() {
        return this.data.content;
    }
    /**
     * Import SPKI as public key.
     * @param algoList - Algorithm list, such as {@link SigningAlgorithmListSlim}.
     */
    async importPublicKey(algoList) {
        const der = asn1.parseVerbose(this.publicKeySpki);
        const errs = {};
        for (const algo of algoList) {
            if (!algo.importSpki) {
                continue;
            }
            try {
                return [algo, await algo.importSpki(this.publicKeySpki, der)];
            }
            catch (err) {
                errs[algo.uuid] = err;
            }
        }
        const errorMsgs = Object.entries(errs).map(([uuid, err]) => `  ${uuid} ${err}`);
        throw new AggregateError(Object.values(errs), `cannot import key\n${errorMsgs.join("\n")}\n(you may need to specify an algoList with more algorithms)`);
    }
}
(function (Certificate) {
    /** Build a certificate from fields. */
    async function build({ name, freshness = 3600000, validity, publicKeySpki, signer, }) {
        assert(CertNaming.isCertName(name));
        const data = new Data();
        data.name = name;
        data.contentType = ContentTypeKEY;
        data.freshnessPeriod = freshness;
        data.sigInfo = new SigInfo(validity);
        data.content = publicKeySpki;
        await signer.sign(data);
        return Certificate.fromData(data);
    }
    Certificate.build = build;
    /** Create a certificated signed by issuer. */
    async function issue(opts) {
        let { issuerPrivateKey: signer, issuerId, publicKey: { name, spki } } = opts;
        name = CertNaming.makeCertName(name, { issuerId });
        if (!spki) {
            throw new Error("options.publicKey.spki unavailable");
        }
        return build({ ...opts, name, publicKeySpki: spki, signer });
    }
    Certificate.issue = issue;
    /** Create a self-signed certificate. */
    async function selfSign(opts) {
        const { privateKey, publicKey: { name: pubName } } = opts;
        if (!privateKey.name.equals(pubName)) {
            throw new Error("key pair mismatch");
        }
        return issue({
            validity: ValidityPeriod.MAX,
            ...opts,
            issuerId: CertNaming.ISSUER_SELF,
            issuerPrivateKey: privateKey,
        });
    }
    Certificate.selfSign = selfSign;
})(Certificate || (Certificate = {}));
